import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextInput,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import HtmlField from '../../components/inputs/htmlField';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const EditorWrap = styled.div`
    margin-top: 4px;
    .DraftEditor-root {
        min-height: 160px;
    }
`;

const DEFAULT_HTML =
    '<p>Hello {{.FirstName}},</p><p><a href="{{.URL}}">Click here</a></p>';

const emptyForm = {
    name: '',
    subject: '',
    textBody: '',
    htmlBody: DEFAULT_HTML,
};

function AwarePhishTemplates() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const { templates, loadingTemplates, savingAsset } = awarePhishStore;

    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const formRef = useRef(form);
    formRef.current = form;

    useEffect(() => {
        if (companyId) awarePhishStore.loadTemplates(companyId);
    }, [companyId]);

    // Stable binding for HtmlField (react-rte); remount via editorKey on open/edit.
    const htmlField = useMemo(
        () => ({
            id: 'aware-phish-html-body',
            label: 'HTML body',
            get value() {
                return formRef.current.htmlBody || '';
            },
            rules: '',
            error: '',
            onChange: (html) => {
                setForm((prev) => ({ ...prev, htmlBody: html }));
            },
        }),
        [editorKey]
    );
    const mrkdownField = useMemo(
        () => ({
            id: 'aware-phish-html-body-md',
            onChange: () => {},
        }),
        [editorKey]
    );

    const startCreate = () => {
        setEditingId(null);
        setForm(emptyForm);
        setEditorKey((k) => k + 1);
        setShowForm(true);
    };

    const startEdit = (t) => {
        if (t.isPlatform) return;
        setEditingId(t.id);
        setForm({
            name: t.name || '',
            subject: t.subject || '',
            textBody: t.textBody || '',
            htmlBody: t.htmlBody || DEFAULT_HTML,
        });
        setEditorKey((k) => k + 1);
        setShowForm(true);
    };

    const save = async () => {
        try {
            await awarePhishStore.saveTemplate(companyId, editingId, form);
            commonStore.success(
                editingId ? 'Template updated' : 'Template created'
            );
            setShowForm(false);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save template'
            );
        }
    };

    const remove = async (t) => {
        if (t.isPlatform) return;
        if (!window.confirm(`Delete template “${t.name}”?`)) return;
        try {
            await awarePhishStore.removeTemplate(companyId, t.id);
            commonStore.success('Template deleted');
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to delete template'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH templates</title>
            </Helmet>
            <HeaderSection
                heading="Email templates"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Company templates plus the platform AwareGO default. Include {{.URL}} for the phishing link.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New template"
                        mode="primary"
                        onClick={startCreate}
                        $noWrap
                    />
                }
            />
            <AwarePhishSubNav active="templates" />

            {showForm ? (
                <Block padding="20" column style={{ marginBottom: 14 }}>
                    <Heading3>
                        {editingId ? 'Edit template' : 'New template'}
                    </Heading3>
                    <FieldLabel>Name</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.name}
                        onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                        }
                    />
                    <FieldLabel>Subject</FieldLabel>
                    <TextInput
                        fullWidth
                        value={form.subject}
                        onChange={(e) =>
                            setForm({ ...form, subject: e.target.value })
                        }
                    />
                    <FieldLabel>HTML body</FieldLabel>
                    <TextTiny lighter style={{ marginBottom: 6 }}>
                        Use {'{{.URL}}'}, {'{{.FirstName}}'}, {'{{.LastName}}'}{' '}
                        as placeholders.
                    </TextTiny>
                    <EditorWrap>
                        <HtmlField
                            key={editorKey}
                            field={htmlField}
                            mrkdownField={mrkdownField}
                            showLabel={false}
                        />
                    </EditorWrap>
                    <FlexDiv gap={12} style={{ marginTop: 16 }}>
                        <ThemedButton
                            text={savingAsset ? 'Saving…' : 'Save'}
                            mode="primary"
                            onClick={save}
                            disabled={savingAsset}
                            $noWrap
                        />
                        <ThemedButton
                            text="Cancel"
                            mode="secondary"
                            onClick={() => setShowForm(false)}
                            $noWrap
                        />
                    </FlexDiv>
                </Block>
            ) : null}

            <Block tablePadding column>
                {loadingTemplates && !(templates || []).length ? (
                    <TextDefault lighter>Loading…</TextDefault>
                ) : !(templates || []).length ? (
                    <TextDefault lighter>
                        No templates yet. Create one, or wait for the platform
                        AwareGO Default to appear after migration.
                    </TextDefault>
                ) : (
                    (templates || []).map((t) => (
                        <FlexDiv
                            key={t.id}
                            justifyBetween
                            alignCenter
                            style={{
                                padding: '12px 0',
                                borderBottom: `1px solid ${palette.graphite.silver}`,
                            }}
                        >
                            <div>
                                <TextDefault bold>
                                    {t.name}
                                    {t.isPlatform ? ' (AwareGO default)' : ''}
                                </TextDefault>
                                <TextTiny lighter>{t.subject}</TextTiny>
                            </div>
                            {!t.isPlatform ? (
                                <FlexDiv gap={8}>
                                    <ThemedButton
                                        text="Edit"
                                        mode="secondary"
                                        onClick={() => startEdit(t)}
                                        $noWrap
                                    />
                                    <ThemedButton
                                        text="Delete"
                                        mode="secondary"
                                        onClick={() => remove(t)}
                                        $noWrap
                                    />
                                </FlexDiv>
                            ) : (
                                <TextTiny lighter>Read-only</TextTiny>
                            )}
                        </FlexDiv>
                    ))
                )}
            </Block>
        </Container>
    );
}

export default observer(AwarePhishTemplates);
